import { Loader2 } from 'lucide-react'
import { useBankAccounts, useFacilities } from '@/hooks/useBanking'
import { ManualBankFormModal, ManualBankModalShell } from './ManualBankFormModal'
import type { ManualBankFormMode } from './manualBankSchema'

export type { ManualBankFormMode } from './manualBankSchema'

interface ManualBankFormDialogProps {
  mode: ManualBankFormMode
  onClose: () => void
  /**
   * Called with the bank's stored name once it is final — after a successful
   * save, and also when some writes landed but another failed.
   */
  onSaved?: (institutionName: string) => void
}

/**
 * Add / edit a manual bank and its accounts or cards. Mount it only while
 * open. Loads what the form needs, then hands over to `ManualBankFormModal`,
 * which seeds the form once from that data.
 */
export function ManualBankFormDialog({ mode, onClose, onSaved }: ManualBankFormDialogProps) {
  // The FULL account list: the duplicate-name guard must see every manual bank.
  const accountsQuery = useBankAccounts()
  const facilitiesQuery = useFacilities({ activeOnly: false })
  const accounts = accountsQuery.data
  const facilities = facilitiesQuery.data

  // Data present wins over an error: a failed BACKGROUND refetch keeps the
  // cached data, and swapping the form for the error frame would drop what the
  // user typed. The error frame is only for a load that never produced data.
  if (accounts && facilities) {
    return (
      <ManualBankFormModal
        mode={mode}
        accounts={accounts}
        facilities={facilities}
        refreshing={accountsQuery.isFetching}
        onClose={onClose}
        onSaved={onSaved}
      />
    )
  }

  // Both loads can fail independently; show every message, not just the first.
  const loadErrors = [accountsQuery.error, facilitiesQuery.error].filter((error) => error !== null)

  return (
    <ManualBankModalShell mode={mode} onClose={onClose} saving={false} submitDisabled>
      {loadErrors.length > 0 ? (
        <p role="alert" className="text-sm text-destructive">
          {loadErrors.map((error) => error.message).join('; ')}
        </p>
      ) : (
        <div className="flex justify-center py-8">
          <Loader2 aria-label="Loading" className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </ManualBankModalShell>
  )
}
